
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Target, Mail, Users, Calendar, Check, Gift, BadgeCheck, Megaphone } from 'lucide-react';

interface BenefitItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const BenefitItem = ({ icon, title, description }: BenefitItemProps) => (
  <div className="flex items-start gap-3 p-4 rounded-lg bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all">
    <div className="mt-1 h-10 w-10 flex-shrink-0 rounded-full bg-medihub-primary/10 flex items-center justify-center">
      {icon}
    </div>
    <div>
      <h4 className="font-bold text-gray-800">{title}</h4>
      <p className="text-gray-600 text-sm mt-1">{description}</p>
    </div>
  </div>
);

const EventListMarketingSection = () => {
  return (
    <div className="my-8">
      <Card className="overflow-hidden border-t-4 border-t-medihub-primary">
        <CardHeader className="bg-gradient-to-r from-medihub-primary/10 to-transparent pb-6">
          <div className="flex flex-wrap gap-2 mb-2">
            <Badge className="bg-medihub-primary">Szervezőknek</Badge>
            <Badge variant="outline" className="bg-white">Prémium funkciók</Badge>
          </div>
          <CardTitle className="text-2xl md:text-3xl">
            Több regisztráló, nagyobb láthatóság
          </CardTitle>
          <CardDescription className="text-base">
            Így segít a platformunk a rendezvényszervezőknek elérni a kívánt célközönséget!
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <BenefitItem
              icon={<TrendingUp className="h-5 w-5 text-medihub-primary" />}
              title="Nagyobb láthatóság"
              description="Kiemelt eseményeid a főoldalon és az ajánlott listák élén jelennek meg."
            />
            <BenefitItem
              icon={<Target className="h-5 w-5 text-medihub-primary" />}
              title="Célzott megjelenés"
              description="Az orvosok és egészségügyi szakemberek pontos szakterületük szerint kapnak értesítést."
            />
            <BenefitItem
              icon={<Mail className="h-5 w-5 text-medihub-primary" />}
              title="Automatikus értesítések"
              description="E-mail értesítésekben a releváns célközönség először a te eseményeidet látja."
            />
            <BenefitItem
              icon={<Calendar className="h-5 w-5 text-medihub-primary" />}
              title="Regisztrációs rendszer"
              description="Online regisztrációs rendszer, QR-kódos check-in és jegykezelési lehetőségek."
            />
            <BenefitItem
              icon={<Megaphone className="h-5 w-5 text-medihub-primary" />}
              title="Automatikus marketing"
              description="Nem kell hirdetésekre költened – mi gondoskodunk a megfelelő célközönség eléréséről."
            />
            <BenefitItem
              icon={<BadgeCheck className="h-5 w-5 text-medihub-primary" />}
              title="Egyszerű adminisztráció"
              description="Egy platformon keresztül kezelheted a résztvevőket és értesítéseket küldhetsz számukra."
            />
          </div>
          
          <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-100">
            <h4 className="font-bold text-lg mb-3">Hogyan működik a fizetős modell?</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-medihub-primary/10 flex items-center justify-center text-sm font-bold">1</div>
                <div>
                  <p className="text-gray-700"><span className="font-medium">Ingyenes eseményfeltöltés</span> – minden szervező felviheti eseményeit az alapcsomagban.</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-medihub-primary/10 flex items-center justify-center text-sm font-bold">2</div>
                <div>
                  <p className="text-gray-700"><span className="font-medium">Prémium listázás</span> – eseményeid kiemelt helyen jelennek meg. (5 000 – 50 000 Ft / esemény)</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-medihub-primary/10 flex items-center justify-center text-sm font-bold">3</div>
                <div>
                  <p className="text-gray-700"><span className="font-medium">Célzott e-mail értesítés</span> – biztosítjuk, hogy a megfelelő emberekhez jusson el az információ.</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-medihub-primary/10 flex items-center justify-center text-sm font-bold">4</div>
                <div>
                  <p className="text-gray-700"><span className="font-medium">Jegyeladás és regisztráció</span> – jutalékos alapon történő teljes körű kezelés.</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EventListMarketingSection;
